import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { NegativeKeywordsManager } from '@/components/tasks/NegativeKeywordsManager';
import { useUserProfile } from '@/contexts/user.context';
import { UserRole } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { SectionHeader } from '@/components/glitch-ui/section-header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useAdminCampaignsQuery } from '@/lib/api-extensions/admin-queries';
import { Spinner } from '@/components/ui/spinner';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';

export const Route = createFileRoute('/_authenticated/_layout/tasks/_layout/negative-keywords')({
  component: NegativeKeywordsPage,
});

function NegativeKeywordsPage() {
  const { user } = useUserProfile();
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: campaigns = [], isLoading } = useAdminCampaignsQuery(organizationData?.id);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const isAdmin = user && user.role === UserRole.ADMIN;

  // Non-admin users shouldn't see this page
  if (!isAdmin) {
    return (
      <div className='pt-6'>
        <Card className='p-6'>
          <div className='text-center p-4'>This page is only accessible to administrators.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className='pt-6 space-y-6'>
      <Card className='p-6'>
        <SectionHeader title='Negative Keywords Analysis' isSubSection={false} />
        <p className='text-sm text-muted-foreground'>
          Analyze search terms to find potential negative keywords that could improve campaign performance
        </p>

        <div className='mt-6 space-y-6'>
          <div className='space-y-2'>
            <Label htmlFor='campaign-select'>Select a Campaign</Label>
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger id='campaign-select'>
                <SelectValue placeholder='Select a campaign' />
              </SelectTrigger>
              <SelectContent>
                {isLoading ? (
                  <div className='flex items-center justify-center p-2'>
                    <Spinner size='sm' />
                  </div>
                ) : campaigns.length === 0 ? (
                  <div className='p-2 text-center text-sm text-muted-foreground'>No campaigns found</div>
                ) : (
                  campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id.toString()}>
                      {campaign.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Show the NegativeKeywordsTab component when a campaign is selected */}
          {selectedCampaign ? (
            <div className='space-y-4'>
              <NegativeKeywordsManager campaignSlug={selectedCampaign} />
            </div>
          ) : (
            <div className='p-6 text-center bg-muted rounded-md'>
              <p className='text-lg font-medium mb-2'>Please select a campaign</p>
              <p className='text-sm text-muted-foreground'>
                Negative keyword analysis requires a specific campaign to be selected.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
